import { useState, useCallback, useMemo } from 'react'
import { VERSION } from '@/version'
import { CHANGELOG } from '../data/changelog'
import type { ChangelogEntry } from '../types/changelog.types'
import { hasUnseenUpdates } from '../utils/version'

const LAST_SEEN_VERSION_KEY = 'arceusx_whats_new_last_seen'
const PAGE_VISITED_VERSION_KEY = 'arceusx_whats_new_page_visited'

/**
 * Hook to manage What's New feature state
 *
 * Determines if user should see:
 * 1. What's New banner - if version > last seen version
 * 2. What's New badge - if version > page visited version
 *
 * Stores preferences in localStorage following arceusx_ prefix convention.
 *
 * @returns {object} What's New state and controls
 */
export function useWhatsNew() {
  // Get last seen version from localStorage
  const [lastSeenVersion, setLastSeenVersion] = useState<string | null>(() => {
    return localStorage.getItem(LAST_SEEN_VERSION_KEY)
  })

  // Get page visited version from localStorage
  const [pageVisitedVersion, setPageVisitedVersion] = useState<string | null>(
    () => {
      return localStorage.getItem(PAGE_VISITED_VERSION_KEY)
    }
  )

  /**
   * Compute if banner should show
   * Show banner if current version > last seen version
   */
  const showBanner = useMemo(() => {
    return hasUnseenUpdates(VERSION, lastSeenVersion)
  }, [lastSeenVersion])

  /**
   * Compute if badge should show
   * Show badge if current version > page visited version
   * Badge is more persistent than banner (requires visiting the page)
   */
  const showBadge = useMemo(() => {
    return hasUnseenUpdates(VERSION, pageVisitedVersion)
  }, [pageVisitedVersion])

  /**
   * Get latest changelog entry (most recent)
   */
  const latestEntry = useMemo<ChangelogEntry | null>(() => {
    return CHANGELOG.length > 0 ? CHANGELOG[0] : null
  }, [])

  /**
   * Group changelog entries by month for What's New page
   * @returns Object with "YYYY-MM" keys and ChangelogEntry[] values
   */
  const entriesByMonth = useMemo(() => {
    const grouped: Record<string, ChangelogEntry[]> = {}

    for (const entry of CHANGELOG) {
      // Extract YYYY-MM from date
      const monthKey = entry.date.substring(0, 7) // "2026-01-29" -> "2026-01"

      if (!grouped[monthKey]) {
        grouped[monthKey] = []
      }

      grouped[monthKey].push(entry)
    }

    return grouped
  }, [])

  /**
   * Dismiss the What's New banner
   * Stores current version in localStorage as "last seen"
   * User won't see banner again until next version update
   */
  const dismissBanner = useCallback(() => {
    localStorage.setItem(LAST_SEEN_VERSION_KEY, VERSION)
    setLastSeenVersion(VERSION)
  }, [])

  /**
   * Mark What's New page as visited
   * Stores current version in localStorage
   * Removes badge indicator from navigation
   */
  const markPageVisited = useCallback(() => {
    localStorage.setItem(PAGE_VISITED_VERSION_KEY, VERSION)
    setPageVisitedVersion(VERSION)

    // Also dismiss banner when page is visited
    if (showBanner) {
      dismissBanner()
    }
  }, [showBanner, dismissBanner])

  /**
   * Reset What's New state (useful for testing/debugging)
   * Clears both localStorage keys
   */
  const resetWhatsNew = useCallback(() => {
    localStorage.removeItem(LAST_SEEN_VERSION_KEY)
    localStorage.removeItem(PAGE_VISITED_VERSION_KEY)
    setLastSeenVersion(null)
    setPageVisitedVersion(null)
  }, [])

  return {
    /** Current app version */
    currentVersion: VERSION,

    /** Should show What's New banner */
    showBanner,

    /** Should show badge on navigation link */
    showBadge,

    /** Latest changelog entry (most recent) */
    latestEntry,

    /** Changelog entries grouped by month */
    entriesByMonth,

    /** Dismiss the banner (stores version in localStorage) */
    dismissBanner,

    /** Mark page as visited (removes badge) */
    markPageVisited,

    /** Reset state (for testing) */
    resetWhatsNew,
  }
}
